import {
  OrganizationRegistrationFormInput,
  generatePassword,
  getOrganizationRegistrationFormDataDefaultValues,
  getOrganizationRegistrationFormDataSchema,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  FormField,
  Input,
  PageName,
  PasswordInput,
  buttonStyles,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  createOrganization,
  removeOrganizationsCache,
  useGetErrorMessage,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/management/organizations/registration")({
  component: Component,
});

function Component() {
  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const defaultValues = useMemo(function (): OrganizationRegistrationFormInput {
    return {
      ...getOrganizationRegistrationFormDataDefaultValues(),
      password: generatePassword(),
    };
  }, []);

  const { Field, Subscribe, handleSubmit, ...form } = useForm({
    defaultValues,
    async onSubmit({ value }) {
      try {
        console.debug("フォームが送信されました。", value);
        const { organization } = await createOrganization(value);

        await $alert({
          color: "success",
          content: t("%Register.2%", { $: { name: organization.name } }),
          title: t("%Register.0%"),
        });

        await navigate({ to: "/management/organizations", ignoreBlocker: true });

        removeOrganizationsCache();
      } catch (error) {
        showError(error);
      }
    },
    validators: {
      onChange: getOrganizationRegistrationFormDataSchema(),
    },
  });

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: t("%Form.1%"),
              no: t("%Stay.0%"),
              title: t("%Form.0%"),
              yes: t("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  return (
    <main>
      <PageName>{t("%OrganizationInformation.0%")}</PageName>
      <p className={css({ fontSize: "24px", fontWeight: 700 })}>
        {t("%OrganizationInformation.0%")}
      </p>
      <form
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginTop: "16px",
        })}
        onSubmit={function (event) {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Field
          children={function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "320px" })}
                id={field.name}
                label={t("%OrganizationName.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
          name="name"
        />
        <Field name="representativeName">
          {function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "160px" })}
                id={field.name}
                label={t("%Representative.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
        </Field>
        <Field name="emailAddress">
          {function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "320px" })}
                id={field.name}
                label={t("%OrganizationEmailAddress.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
        </Field>
        <Field
          children={function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ maxWidth: "160px" })}
                id={field.name}
                label={t("%EmailAddressLogin.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.checked)}
                title=""
                type="checkbox"
                value={field.state.value.toString()}
              />
            );
          }}
          name="emailAddressLogin"
        />
        <Field name="password">
          {function (field) {
            return (
              <FormField
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                invalid={field.state.meta.errors.length > 0}
                label={t("%Password.0%")}
                required
              >
                <PasswordInput
                  id={field.name}
                  onBlur={() => field.handleBlur()}
                  onChange={(event) => field.handleChange(event.target.value)}
                  value={field.state.value}
                />
              </FormField>
            );
          }}
        </Field>
        <Field name="postalCode">
          {function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                containerClassName={css({ maxWidth: "120px" })}
                id={field.name}
                label={t("%PostalCode.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
        </Field>
        <Field name="address">
          {function (field) {
            return (
              <Input
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                containerClassName={css({ maxWidth: "480px" })}
                id={field.name}
                label={t("%Address.0%")}
                onBlur={() => field.handleBlur()}
                onChange={(event) => field.handleChange(event.target.value)}
                required
                title=""
                value={field.state.value}
              />
            );
          }}
        </Field>

        <div className={css({ display: "flex", gap: "8px" })}>
          <Subscribe
            selector={function ({ isSubmitting }) {
              return isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <Link
                  className={buttonStyles({ color: "secondary" })}
                  disabled={submitting}
                  to="/management/organizations"
                >
                  {t("%Cancel.0%")}
                </Link>
              );
            }}
          </Subscribe>
          <Subscribe
            selector={function ({ canSubmit, isSubmitting }) {
              return !canSubmit || isSubmitting;
            }}
          >
            {function (disabled) {
              return (
                <Button className={css({ width: "max-content" })} disabled={disabled} type="submit">
                  {t("%Save.0%")}
                </Button>
              );
            }}
          </Subscribe>
        </div>
      </form>
    </main>
  );
}
